import type { KeyboardEvent, PointerEvent } from 'react'
import {
  DEFAULT_PRODUCT_IMAGE_FRAMING,
  PRODUCT_IMAGE_FRAMING_LIMITS,
  type ProductImageFraming,
} from '../../shared/productPresentation'
import FramedProductImage from '../components/FramedProductImage'
import { AdminButton, AdminField } from './AdminUi'
import '../styles/product-image-framing-editor.css'

type ProductImageFramingEditorProps = {
  src: string
  alt: string
  framing: ProductImageFraming
  onChange: (framing: ProductImageFraming) => void
  disabled?: boolean
  label?: string
  primarySurfaces?: boolean
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

export default function ProductImageFramingEditor({
  src,
  alt,
  framing,
  onChange,
  disabled = false,
  label = 'Product image framing',
  primarySurfaces = true,
}: ProductImageFramingEditorProps) {
  function updateFocalFromPointer(event: PointerEvent<HTMLDivElement>) {
    if (disabled) return
    const rect = event.currentTarget.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const focalX = clamp((event.clientX - rect.left) / rect.width, 0, 1)
    const focalY = clamp((event.clientY - rect.top) / rect.height, 0, 1)
    onChange({
      ...framing,
      focalX: Number(focalX.toFixed(4)),
      focalY: Number(focalY.toFixed(4)),
    })
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (disabled) return
    event.currentTarget.setPointerCapture(event.pointerId)
    updateFocalFromPointer(event)
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (disabled || !event.currentTarget.hasPointerCapture(event.pointerId)) return
    updateFocalFromPointer(event)
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return
    const step = event.shiftKey ? 0.05 : 0.01
    let focalX = framing.focalX
    let focalY = framing.focalY
    if (event.key === 'ArrowLeft') focalX -= step
    else if (event.key === 'ArrowRight') focalX += step
    else if (event.key === 'ArrowUp') focalY -= step
    else if (event.key === 'ArrowDown') focalY += step
    else if (event.key === 'Home') {
      focalX = 0.5
      focalY = 0.5
    } else {
      return
    }
    event.preventDefault()
    onChange({
      ...framing,
      focalX: Number(clamp(focalX, 0, 1).toFixed(4)),
      focalY: Number(clamp(focalY, 0, 1).toFixed(4)),
    })
  }

  return (
    <div className="product-image-framing-editor" aria-label={label}>
      <div className="product-image-framing-editor__picker-column">
        <strong>Choose what matters</strong>
        <p>Click or drag the target onto the part of the product customers should keep seeing.</p>
        <div
          className="product-image-framing-editor__picker"
          role="group"
          aria-label="Image focal point"
          aria-valuetext={`Horizontal ${Math.round(framing.focalX * 100)}%, vertical ${Math.round(framing.focalY * 100)}%`}
          tabIndex={disabled ? -1 : 0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onKeyDown={onKeyDown}
        >
          <img src={src} alt="" draggable={false} />
          <span
            className="product-image-framing-editor__target"
            style={{
              left: `${framing.focalX * 100}%`,
              top: `${framing.focalY * 100}%`,
            }}
            aria-hidden="true"
          />
        </div>
        <small>Arrow keys move 1% · Shift + arrows move 5% · Home recentres.</small>
      </div>

      <div className="product-image-framing-editor__controls">
        <AdminField label={`Zoom · ${framing.zoom.toFixed(2)}×`}>
          <input
            className="admin-content-media__range"
            type="range"
            min={PRODUCT_IMAGE_FRAMING_LIMITS.minZoom}
            max={PRODUCT_IMAGE_FRAMING_LIMITS.maxZoom}
            step="0.01"
            value={framing.zoom}
            disabled={disabled}
            onChange={(event) => onChange({ ...framing, zoom: Number(event.target.value) })}
          />
        </AdminField>
        <div className="product-image-framing-editor__readout" aria-label="Focal point values">
          <span>X {Math.round(framing.focalX * 100)}%</span>
          <span>Y {Math.round(framing.focalY * 100)}%</span>
        </div>
        <AdminButton
          variant="ghost"
          compact
          type="button"
          disabled={disabled}
          onClick={() => onChange({ ...DEFAULT_PRODUCT_IMAGE_FRAMING })}
        >
          Reset framing
        </AdminButton>
      </div>

      <div className={`product-image-framing-editor__previews ${primarySurfaces ? 'is-primary' : 'is-detail-only'}`}>
        {primarySurfaces && (
          <figure>
            <strong>Homepage card</strong>
            <div className="product-image-framing-editor__preview product-image-framing-editor__preview--homepage">
              <FramedProductImage src={src} alt={alt} framing={framing} />
            </div>
          </figure>
        )}
        {primarySurfaces && (
          <figure>
            <strong>Order card</strong>
            <div className="product-image-framing-editor__preview product-image-framing-editor__preview--order">
              <FramedProductImage src={src} alt={alt} framing={framing} />
            </div>
          </figure>
        )}
        <figure>
          <strong>Desktop detail</strong>
          <div className="product-image-framing-editor__preview product-image-framing-editor__preview--desktop-detail">
            <FramedProductImage src={src} alt={alt} framing={framing} />
          </div>
        </figure>
        <figure>
          <strong>Mobile detail</strong>
          <div className="product-image-framing-editor__preview product-image-framing-editor__preview--mobile-detail">
            <FramedProductImage src={src} alt={alt} framing={framing} />
          </div>
        </figure>
      </div>
    </div>
  )
}
